import React from 'react';
import Loader from '../../components/Loader';
import PhototherapyIsraeliCurves from './PhototherapyIsraeliCurves';
import PhototherapyAap2022 from './aap2022/PhototherapyAap2022';
import { usePhototherapySettings } from './phototherapyConfig';
import type { PhototherapyVariant } from './variants';
import './phototherapy.scss';

interface PhototherapyAppProps {
  /** Hospital slug from the route (`/main/:hospitalName/:appToken`). */
  hospital?: string;
  /** Which registered app this is — decides the AppConfig row and the default. */
  variant: PhototherapyVariant;
}

/**
 * Root of the jaundice calculator, shared by every app in `variants.ts`. Reads
 * the hospital's `<appKey>/settings` row and mounts one of two very different
 * calculators:
 *
 *  - `israeliCurves` — the original Israeli curves.
 *  - `aap2022`       — the AAP 2022 curves the Israeli Neonatology Association
 *                      now recommends, with hospital-tunable follow-up rules.
 *
 * Which of them a hospital with no stored row gets is the variant's
 * `defaultProtocol`; both are reachable from either app's admin page.
 *
 * If the settings cannot be read this renders a retry prompt and NO calculator.
 * Guessing would mean showing one hospital's staff another hospital's treatment
 * thresholds, so an empty screen with an explanation is the safe outcome.
 */
export const PhototherapyApp: React.FC<PhototherapyAppProps> = ({ hospital, variant }) => {
  const { settings, loading, error, retry } = usePhototherapySettings(hospital, variant);

  if (loading) {
    return (
      <div className="phototherapy-gate" data-page-title={variant.title} data-testid="phototherapy-loading">
        <div className="phototherapy-gate-loader">
          <Loader isLoading={true} />
        </div>
        <p className="phototherapy-gate-note">טוען את הגדרות המחלקה...</p>
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="phototherapy-gate" data-page-title={variant.title} data-testid="phototherapy-settings-error">
        <div className="phototherapy-gate-card">
          <h2>לא ניתן לטעון את הגדרות המחשבון</h2>
          <p>
            המחשבון מציג עקומות טיפול לפי ההגדרות של המחלקה, ולכן הוא אינו נפתח לפני שההגדרות
            נטענות. יש לבדוק את החיבור לרשת ולנסות שוב.
          </p>
          <button type="button" onClick={retry} className="phototherapy-gate-retry">
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  if (settings.protocol === 'aap2022') {
    return (
      <PhototherapyAap2022
        settings={settings.aap2022}
        protocolFiles={settings.protocolFiles}
        pageTitle={variant.title}
      />
    );
  }
  return <PhototherapyIsraeliCurves pageTitle={variant.title} />;
};

export default PhototherapyApp;
